'use client'

import { MouseEvent, useState } from 'react'
import { Cell, Icon } from '@vinted/web-ui'
import { PriceTag24, Calendar24 } from '@vinted/monochrome-icons'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  OppositeUser,
  reserveTransactionItems,
  TransactionModel,
  ApplicableTransactionAction,
  AdditionalTransactionAction,
} from '@marketplace-web/messaging/conversation-messaging-data'

import { MARK_AS_SOLD_URL } from 'constants/routes'

import ConfirmModal from '../ConfirmModal'

enum OrderActionModal {
  Reserve,
  RemoveReservation,
}

type Props = {
  transaction?: TransactionModel | null
  oppositeUser?: OppositeUser | null
}

const OrderActions = ({ transaction, oppositeUser }: Props) => {
  const translate = useTranslate('conversation.conversation_info')

  const [visibleModalName, setVisibleModalName] = useState<OrderActionModal | null>(null)
  const [isReserveInProgress, setIsReserveInProgress] = useState(false)

  if (!transaction?.availableActions) return null

  const { availableActions } = transaction

  function handleReserveClick() {
    setVisibleModalName(OrderActionModal.Reserve)
  }

  async function handleReserveModalConfirmClick() {
    if (!transaction || !oppositeUser || isReserveInProgress) return

    setIsReserveInProgress(true)

    await reserveTransactionItems(transaction.id, true)

    setIsReserveInProgress(false)

    setVisibleModalName(null)
  }

  function handleReserveModalCancelClick() {
    setVisibleModalName(null)
  }

  async function removeItemReservation() {
    if (!transaction?.order || isReserveInProgress) return

    setIsReserveInProgress(true)

    await reserveTransactionItems(transaction.id, false)

    setIsReserveInProgress(false)
  }

  async function handleRemoveReservationModalConfirmClick() {
    await removeItemReservation()

    setVisibleModalName(null)
  }

  function handleRemoveReservationModalCancelClick() {
    setVisibleModalName(null)
  }

  function handleReservationRemoval(event: MouseEvent) {
    event.preventDefault()

    setVisibleModalName(OrderActionModal.RemoveReservation)
  }

  function renderMarkAsSoldAction() {
    if (!availableActions.includes(AdditionalTransactionAction.MarkAsSold)) return null

    if (!transaction) return null

    return (
      <Cell
        title={translate('transaction_actions.mark_as_sold')}
        url={MARK_AS_SOLD_URL(transaction.id)}
        prefix={<Icon name={PriceTag24} color={Icon.Color.Primary} />}
        type={Cell.Type.Navigating}
      />
    )
  }

  function renderRemoveReservationModal() {
    return (
      <ConfirmModal
        show={visibleModalName === OrderActionModal.RemoveReservation}
        theme="primary"
        translationsPrefix="conversation.conversation_info.remove_reservation_modal"
        onConfirm={handleRemoveReservationModalConfirmClick}
        onCancel={handleRemoveReservationModalCancelClick}
      />
    )
  }

  function renderReserveModal() {
    return (
      <ConfirmModal
        show={visibleModalName === OrderActionModal.Reserve}
        theme="primary"
        translationsPrefix="conversation.conversation_info.reserve_modal"
        onConfirm={handleReserveModalConfirmClick}
        onCancel={handleReserveModalCancelClick}
      />
    )
  }

  function renderReserveAction() {
    if (!availableActions.includes(ApplicableTransactionAction.Reserve)) return null

    if (!transaction) return null

    if (transaction.order?.reserved) {
      return (
        <>
          <Cell
            title={translate('transaction_actions.unreserve')}
            prefix={<Icon name={Calendar24} color={Icon.Color.Primary} />}
            onClick={handleReservationRemoval}
            type={Cell.Type.Navigating}
          />
          {renderRemoveReservationModal()}
        </>
      )
    }

    return (
      <>
        <Cell
          title={translate('transaction_actions.reserve')}
          onClick={handleReserveClick}
          prefix={<Icon name={Calendar24} color={Icon.Color.Primary} />}
          type={Cell.Type.Navigating}
        />
        {renderReserveModal()}
      </>
    )
  }

  return (
    <>
      {renderMarkAsSoldAction()}
      {renderReserveAction()}
    </>
  )
}

export default OrderActions
